import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, LayoutDashboard, Users, FileClock, LogOut, AlertTriangle, Package, Bell, Trash2, Menu, X, Settings as SettingsIcon, DollarSign, Activity, Send, KanbanSquare, UsersRound, Server, FileText, Receipt, CreditCard, Headset, Megaphone } from "lucide-react";
import { OplyxLogo } from "@/components/brand/OplyxLogo";
import { ThemeToggle } from "@/components/brand/ThemeToggle";

export default function AdminShell() {
  const user = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
  const nav = useNavigate();
  const location = useLocation();
  const [ok, setOk] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [alertSample, setAlertSample] = useState([]);
  const [showPopover, setShowPopover] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(r => {
      setOk(!!r.data?.is_platform_admin);
      if (!r.data?.is_platform_admin) nav("/");
    }).catch(() => {
      setOk(false); nav("/auth");
    });
  }, [nav]);

  useEffect(() => {
    if (ok !== true) return;
    const load = () =>
      api.get("/admin/alerts")
        .then(r => { setAlertCount(r.data.length); setAlertSample(r.data.slice(0, 4)); })
        .catch(() => {});
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [ok]);

  // close popovers/drawer on route change
  useEffect(() => {
    setShowPopover(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileNavOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileNavOpen]);

  const logout = () => { localStorage.clear(); nav("/auth"); };

  if (ok === null) return <div className="p-10 text-sm text-muted-foreground">Validando permissões...</div>;
  if (!ok) return <Navigate to="/" replace />;

  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Visão geral", end: true },
    { to: "/admin/support", icon: Headset, label: "Chamados" },
    { to: "/admin/clients", icon: Users, label: "Clientes" },
    { to: "/admin/fiscal", icon: Receipt, label: "Fiscal (global)" },
    { to: "/admin/onboarding", icon: KanbanSquare, label: "Implantação" },
    { to: "/admin/alerts", icon: AlertTriangle, label: "Alertas" },
    { to: "/admin/plans", icon: Package, label: "Planos" },
    { to: "/admin/finance", icon: DollarSign, label: "Financeiro" },
    { to: "/admin/cobranca", icon: CreditCard, label: "Central de Cobrança" },
    { to: "/admin/billing-payments", icon: FileText, label: "Pagamentos & NFS-e" },
    { to: "/admin/ops", icon: Activity, label: "Operacional" },
    { to: "/admin/messages", icon: Send, label: "Comunicação" },
    { to: "/admin/referrals", icon: Megaphone, label: "Indicações" },
    { to: "/admin/team", icon: UsersRound, label: "Time" },
    { to: "/admin/infra-health", icon: Server, label: "Infraestrutura" },
    { to: "/admin/juridico", icon: ShieldCheck, label: "Jurídico" },
    { to: "/admin/settings", icon: SettingsIcon, label: "Configurações" },
    { to: "/admin/audit", icon: FileClock, label: "Auditoria" },
    { to: "/admin/danger-zone", icon: Trash2, label: "Danger Zone", danger: true },
  ];

  // Conteúdo de navegação compartilhado entre desktop e mobile drawer
  const NavContent = ({ onItemClick }) => (
    <>
      <div className="flex items-center justify-between gap-2 px-6 py-5">
        <div className="flex flex-col leading-tight">
          <OplyxLogo size={20} tone="adaptive" className="text-base" />
          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5 font-semibold uppercase tracking-[0.18em]">Platform Admin</div>
        </div>
        {/* Botão de fechar no drawer mobile */}
        <button
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden p-2 -mr-2 rounded-full hover:bg-muted text-foreground"
          data-testid="admin-mobile-nav-close"
          aria-label="Fechar menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto" data-testid="admin-nav">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onItemClick}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/ /g,"-")}`}
            className={({ isActive }) =>
              `flex items-center justify-between gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? (it.danger ? "bg-rose-700 text-white" : "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900")
                  : (it.danger
                      ? "text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-700 dark:hover:text-rose-300"
                      : "text-foreground hover:bg-muted")
              }`
            }
          >
            <span className="flex items-center gap-3"><it.icon className="w-4 h-4" />{it.label}</span>
            {it.to === "/admin/alerts" && alertCount > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center px-1" data-testid="admin-alert-badge">
                {alertCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border space-y-1">
        <div className="px-2 py-2 text-xs text-foreground">
          <div className="font-semibold truncate">{user.name || user.email}</div>
          <div className="text-muted-foreground truncate">{user.email}</div>
        </div>
        <Button data-testid="admin-logout" variant="ghost" size="sm" className="w-full justify-start text-foreground hover:bg-muted" onClick={logout}>
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar desktop fixa (≥lg) */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card">
        <NavContent />
      </aside>

      {/* Drawer mobile (slide-in) — Iter 109 */}
      {mobileNavOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/70 z-[80] backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
            data-testid="admin-mobile-nav-backdrop"
          />
          <aside
            className="lg:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] flex flex-col bg-card border-r border-border z-[90] shadow-2xl animate-in slide-in-from-left duration-200"
            data-testid="admin-mobile-nav-drawer"
          >
            <NavContent />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-border bg-card/60 backdrop-blur flex items-center justify-between px-4 md:px-8 gap-3 relative">
          {/* Botão hamburger — só mobile */}
          <button
            onClick={() => setMobileNavOpen(true)}
            className="lg:hidden p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
            data-testid="admin-mobile-nav-toggle"
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5 text-foreground" />
          </button>
          {/* Título compacto na barra mobile */}
          <div className="lg:hidden flex items-center gap-2 text-sm font-medium text-foreground">
            <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Platform Admin</span>
          </div>
          {/* spacer no desktop empurra os itens à direita */}
          <div className="hidden lg:block flex-1" />

          {/* O painel segue o tema do app (claro/escuro), como as telas dos tenants. */}
          <ThemeToggle />
          <button
            onClick={() => setShowPopover(v => !v)}
            className="relative p-2 rounded-full hover:bg-muted transition-colors"
            data-testid="admin-bell-btn"
            aria-label="Alertas"
          >
            <Bell className={`w-5 h-5 ${alertCount > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} />
            {alertCount > 0 && (
              <span className="absolute top-0 right-0 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center px-1" data-testid="admin-bell-count">
                {alertCount > 99 ? "99+" : alertCount}
              </span>
            )}
          </button>
          {showPopover && (
            <div className="absolute top-14 right-4 md:right-8 w-80 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-2xl z-40 overflow-hidden" data-testid="admin-bell-popover">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="font-semibold text-sm">Alertas ativos</span>
                <span className="text-xs text-muted-foreground">{alertCount}</span>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {alertSample.length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">Nenhum alerta ativo 🎉</div>
                )}
                {alertSample.map((a, i) => (
                  <div key={i} className="px-4 py-3 border-b border-border hover:bg-muted/50 cursor-pointer" onClick={() => { setShowPopover(false); nav("/admin/alerts"); }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs uppercase font-semibold text-amber-600 dark:text-amber-400 truncate">{a.type}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.severity === "high" ? "bg-rose-600" : "bg-amber-600"}`}>{a.severity}</span>
                    </div>
                    <div className="text-sm font-medium truncate mt-0.5">{a.business_name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.message}</div>
                  </div>
                ))}
              </div>
              {alertCount > 0 && (
                <button onClick={() => { setShowPopover(false); nav("/admin/alerts"); }} className="w-full py-2 text-center text-sm text-amber-600 dark:text-amber-400 hover:bg-muted transition-colors border-t border-border" data-testid="admin-bell-view-all">
                  Ver todos os alertas →
                </button>
              )}
            </div>
          )}
        </header>
        <div className="flex-1 p-4 md:p-8 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
